import React, { useEffect, useMemo, useState } from 'react';
import './MarkUsedModal.css';

function MarkUsedModal({
  isOpen,
  loading = false,
  submitting = false,
  context = {},
  pieces = [],
  onClose,
  onSubmit,
}) {
  const [selection, setSelection] = useState({});
  const [componentSelection, setComponentSelection] = useState({});
  const [error, setError] = useState('');

  const pendingQty = Number(context?.pendingQty || 0);
  const allowMulti = pendingQty > 15;
  const componentCandidates = useMemo(
    () => (Array.isArray(context?.componentCandidates) ? context.componentCandidates : []),
    [context?.componentCandidates]
  );
  const componentMode = String(context?.allocationMode || '').toUpperCase() === 'COMPONENT_STOCK' && componentCandidates.length > 0;
  const displayPieces = useMemo(() => {
    const normalizedPieces = Array.isArray(pieces) ? pieces : [];
    if (allowMulti) return normalizedPieces;
    return normalizedPieces.filter((p) => Number(p?.length || 0) + 1e-9 >= pendingQty);
  }, [allowMulti, pieces, pendingQty]);

  const bestFitPieceId = useMemo(() => {
    if (!displayPieces.length) return '';
    if (!allowMulti) {
      const sorted = [...displayPieces].sort((a, b) => (Number(a.length || 0) - pendingQty) - (Number(b.length || 0) - pendingQty));
      return sorted[0]?.pieceId || '';
    }
    const sorted = [...displayPieces].sort((a, b) => Number(b.length || 0) - Number(a.length || 0));
    return sorted[0]?.pieceId || '';
  }, [allowMulti, displayPieces, pendingQty]);

  const selectedQty = useMemo(() => (
    Object.values(selection).reduce((sum, value) => sum + Number(value || 0), 0)
  ), [selection]);
  const remainingQty = useMemo(() => Math.max(0, pendingQty - selectedQty), [pendingQty, selectedQty]);

  useEffect(() => {
    if (!isOpen) return;
    setSelection({});
    setComponentSelection({});
    setError('');
  }, [isOpen, context?.orderId, context?.sku]);

  const canConfirm = useMemo(() => {
    if (pendingQty <= 0) return false;
    if (componentMode) {
      return componentCandidates.every((component) => {
        const componentSku = String(component?.componentSku || '').trim().toUpperCase();
        const requiredQty = Number(component?.requiredQty || 0);
        const componentPieces = Array.isArray(component?.pieces) ? component.pieces : [];
        const selected = componentSelection[componentSku] || {};
        const selectedIds = Object.keys(selected);
        if (!componentSku || requiredQty <= 0 || selectedIds.length === 0) return false;
        if (requiredQty <= 15 && selectedIds.length !== 1) return false;
        const total = selectedIds.reduce((sum, pieceId) => {
          const piece = componentPieces.find((p) => String(p.pieceId) === String(pieceId));
          const qty = Number(selected[pieceId] || 0);
          if (!piece || qty <= 0 || qty > Number(piece.length || 0) + 1e-9) return Number.NaN;
          return sum + qty;
        }, 0);
        return Number.isFinite(total) && Math.abs(total - requiredQty) < 0.0001;
      });
    }
    const selectedIds = Object.keys(selection);
    if (selectedIds.length === 0) return false;
    if (!allowMulti) {
      if (selectedIds.length !== 1) return false;
      const pieceId = selectedIds[0];
      const piece = displayPieces.find((p) => String(p.pieceId) === String(pieceId));
      const qty = Number(selection[pieceId] || 0);
      if (!piece || qty <= 0 || qty > Number(piece.length || 0) + 1e-9) return false;
      return Math.abs(qty - pendingQty) < 0.0001;
    }
    const withinLimits = selectedIds.every((pieceId) => {
      const piece = displayPieces.find((p) => String(p.pieceId) === String(pieceId));
      const qty = Number(selection[pieceId] || 0);
      return !!piece && qty > 0 && qty <= Number(piece.length || 0) + 1e-9;
    });
    if (!withinLimits) return false;
    return Math.abs(selectedQty - pendingQty) < 0.0001;
  }, [allowMulti, componentCandidates, componentMode, componentSelection, displayPieces, pendingQty, selectedQty, selection]);

  const onPieceToggle = (piece) => {
    const pieceId = String(piece?.pieceId || '').trim();
    if (!pieceId) return;
    const pieceLength = Number(piece?.length || 0);
    setError('');
    if (!allowMulti) {
      if (pieceLength + 1e-9 < pendingQty) return;
      setSelection({ [pieceId]: String(pendingQty) });
      return;
    }
    setSelection((prev) => {
      const next = { ...prev };
      if (Object.prototype.hasOwnProperty.call(next, pieceId)) {
        delete next[pieceId];
        return next;
      }
      const already = Object.values(next).reduce((sum, value) => sum + Number(value || 0), 0);
      const remaining = Math.max(0, pendingQty - already);
      const prefill = Math.min(pieceLength, remaining > 0 ? remaining : pieceLength);
      next[pieceId] = String(prefill > 0 ? prefill : 0);
      return next;
    });
  };

  const onQtyChange = (pieceId, rawValue) => {
    const numeric = Number(rawValue || 0);
    setError('');
    setSelection((prev) => ({ ...prev, [pieceId]: Number.isFinite(numeric) && numeric >= 0 ? String(numeric) : '' }));
  };

  const getComponentDisplayPieces = (component) => {
    const requiredQty = Number(component?.requiredQty || 0);
    const componentPieces = Array.isArray(component?.pieces) ? component.pieces : [];
    if (requiredQty > 15) return componentPieces;
    return componentPieces.filter((p) => Number(p?.length || 0) + 1e-9 >= requiredQty);
  };

  const getComponentBestFitPieceId = (component) => {
    const requiredQty = Number(component?.requiredQty || 0);
    const componentPieces = getComponentDisplayPieces(component);
    if (!componentPieces.length) return '';
    if (requiredQty <= 15) {
      const sorted = [...componentPieces].sort((a, b) => (Number(a.length || 0) - requiredQty) - (Number(b.length || 0) - requiredQty));
      return sorted[0]?.pieceId || '';
    }
    const sorted = [...componentPieces].sort((a, b) => Number(b.length || 0) - Number(a.length || 0));
    return sorted[0]?.pieceId || '';
  };

  const onComponentPieceToggle = (component, piece) => {
    const componentSku = String(component?.componentSku || '').trim().toUpperCase();
    const requiredQty = Number(component?.requiredQty || 0);
    const pieceId = String(piece?.pieceId || '').trim();
    if (!componentSku || !pieceId || requiredQty <= 0) return;
    const pieceLength = Number(piece?.length || 0);
    setError('');
    setComponentSelection((prev) => {
      const next = { ...prev };
      const current = { ...(next[componentSku] || {}) };
      if (requiredQty <= 15) {
        if (pieceLength + 1e-9 < requiredQty) return prev;
        next[componentSku] = { [pieceId]: String(requiredQty) };
        return next;
      }
      if (Object.prototype.hasOwnProperty.call(current, pieceId)) {
        delete current[pieceId];
      } else {
        const already = Object.values(current).reduce((sum, value) => sum + Number(value || 0), 0);
        const remaining = Math.max(0, requiredQty - already);
        const prefill = Math.min(pieceLength, remaining > 0 ? remaining : pieceLength);
        current[pieceId] = String(prefill > 0 ? prefill : 0);
      }
      next[componentSku] = current;
      return next;
    });
  };

  const onComponentQtyChange = (componentSku, pieceId, rawValue) => {
    const numeric = Number(rawValue || 0);
    setError('');
    setComponentSelection((prev) => ({
      ...prev,
      [componentSku]: {
        ...(prev[componentSku] || {}),
        [pieceId]: Number.isFinite(numeric) && numeric >= 0 ? String(numeric) : '',
      },
    }));
  };

  const componentSelectedQty = (componentSku) => {
    const selected = componentSelection[componentSku] || {};
    return Object.values(selected).reduce((sum, value) => sum + Number(value || 0), 0);
  };

  const submit = async () => {
    if (!canConfirm) {
      setError('Select piece(s) and match exact pending quantity before confirming.');
      return;
    }
    if (componentMode) {
      const consume = componentCandidates.flatMap((component) => {
        const componentSku = String(component?.componentSku || '').trim().toUpperCase();
        const componentPieces = Array.isArray(component?.pieces) ? component.pieces : [];
        const selected = componentSelection[componentSku] || {};
        return Object.entries(selected).map(([pieceId, qty]) => {
          const piece = componentPieces.find((p) => String(p.pieceId) === String(pieceId));
          return {
            componentSku,
            stockPieceId: pieceId,
            stockSku: String(piece?.stockSku || componentSku).trim().toUpperCase(),
            qty: Number(qty || 0),
          };
        });
      }).filter((x) => x.componentSku && x.stockPieceId && Number.isFinite(x.qty) && x.qty > 0);
      if (consume.length === 0) {
        setError('No component piece quantity selected.');
        return;
      }
      await onSubmit(consume);
      return;
    }
    const consume = Object.entries(selection)
      .map(([pieceId, qty]) => {
        const piece = displayPieces.find((p) => String(p.pieceId) === String(pieceId));
        return {
          stockPieceId: pieceId,
          stockSku: String(piece?.stockSku || context?.sku || '').trim().toUpperCase(),
          qty: Number(qty || 0),
        };
      })
      .filter((x) => x.stockPieceId && Number.isFinite(x.qty) && x.qty > 0);
    if (consume.length === 0) {
      setError('No piece quantity selected.');
      return;
    }
    await onSubmit(consume);
  };

  if (!isOpen) return null;

  return (
    <div className="mu-modal-overlay" onClick={onClose}>
      <div className="mu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mu-head">
          <div className="mu-image-wrap">
            {context?.productImage ? (
              <img src={context.productImage} alt={context?.productName || context?.sku || 'Product'} className="mu-image" />
            ) : (
              <div className="mu-image-placeholder">No Image</div>
            )}
          </div>
          <div className="mu-head-main">
            <h3>Mark Used: {context?.sku || '-'}</h3>
            <div className="mu-sub">Order: {context?.orderNumber || '-'} ({context?.orderId || '-'})</div>
            <div className="mu-sub">{context?.productName || '-'}</div>
            <div className="mu-chips">
              <span>Required: {Number(context?.orderedQty || 0).toFixed(2)}m</span>
              <span>Received: {Number(context?.receivedQty || 0).toFixed(2)}m</span>
              <span>Pending: {Number(pendingQty || 0).toFixed(2)}m</span>
              <span>{allowMulti ? 'Rule: > 15m multi-piece' : 'Rule: <= 15m single-piece'}</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="mu-sub">Loading pieces...</div>
        ) : (
          <>
            <div className="mu-step">Step 1: Select Piece ID</div>
            {componentMode ? (
              <div className="mu-component-stack">
                {componentCandidates.map((component) => {
                  const componentSku = String(component?.componentSku || '').trim().toUpperCase();
                  const requiredQty = Number(component?.requiredQty || 0);
                  const selectedQtyForComponent = componentSelectedQty(componentSku);
                  const remainingForComponent = Math.max(0, requiredQty - selectedQtyForComponent);
                  const componentPieces = getComponentDisplayPieces(component);
                  const bestComponentPieceId = getComponentBestFitPieceId(component);
                  const selectedForComponent = componentSelection[componentSku] || {};
                  return (
                    <div key={componentSku} className="mu-component-section">
                      <div className="mu-component-head">
                        <strong>{componentSku}</strong>
                        <span>Required: {requiredQty.toFixed(2)}m</span>
                        <span>Available: {Number(component?.availableQty || 0).toFixed(2)}m</span>
                        <span>Remaining: {remainingForComponent.toFixed(2)}m</span>
                      </div>
                      <div className="mu-piece-grid">
                        {componentPieces.map((piece) => (
                          <button
                            key={`${componentSku}_${piece.pieceId}`}
                            type="button"
                            className={`mu-piece-card ${Object.prototype.hasOwnProperty.call(selectedForComponent, String(piece.pieceId || '')) ? 'selected' : ''}`}
                            onClick={() => onComponentPieceToggle(component, piece)}
                          >
                            <div className="mu-piece-top">
                              <strong>{piece.pieceId}</strong>
                              {bestComponentPieceId && bestComponentPieceId === piece.pieceId && <span className="mu-best">Best Fit</span>}
                            </div>
                            <div>Current Qty: {Number(piece.length || 0).toFixed(2)}m</div>
                            {piece?.fromGroupedSku && piece?.stockSku && (
                              <div>Stock SKU: {piece.stockSku}</div>
                            )}
                            <div>Location: {piece.location || '-'}</div>
                          </button>
                        ))}
                        {componentPieces.length === 0 && (
                          <div className="mu-sub">No selectable piece for this component.</div>
                        )}
                      </div>
                      {requiredQty > 15 && Object.keys(selectedForComponent).length > 0 && (
                        <div className="mu-qty-grid">
                          {Object.keys(selectedForComponent).map((pieceId) => {
                            const piece = componentPieces.find((p) => String(p.pieceId) === String(pieceId));
                            return (
                              <div key={`${componentSku}_${pieceId}`} className="mu-qty-card">
                                <div><strong>{pieceId}</strong></div>
                                <div className="mu-sub">Current Qty: {Number(piece?.length || 0).toFixed(2)}m</div>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={String(piece?.length || 0)}
                                  value={selectedForComponent[pieceId] || ''}
                                  onChange={(e) => onComponentQtyChange(componentSku, pieceId, e.target.value)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mu-piece-grid">
                {displayPieces.map((piece) => (
                  <button
                    key={piece.pieceId}
                    type="button"
                    className={`mu-piece-card ${Object.prototype.hasOwnProperty.call(selection, String(piece.pieceId || '')) ? 'selected' : ''}`}
                    onClick={() => onPieceToggle(piece)}
                  >
                    <div className="mu-piece-top">
                      <strong>{piece.pieceId}</strong>
                      {bestFitPieceId && bestFitPieceId === piece.pieceId && <span className="mu-best">Best Fit</span>}
                    </div>
                    <div>Current Qty: {Number(piece.length || 0).toFixed(2)}m</div>
                    {piece?.fromGroupedSku && piece?.stockSku && (
                      <div>Stock SKU: {piece.stockSku}</div>
                    )}
                    <div>Location: {piece.location || '-'}</div>
                  </button>
                ))}
                {displayPieces.length === 0 && (
                  <div className="mu-sub">No selectable piece for current rule.</div>
                )}
              </div>
            )}

            <div className="mu-step">Step 2: Quantity</div>
            {componentMode ? (
              <div className="mu-fixed">
                <label>Use Qty (Fixed per component)</label>
                <input readOnly value={canConfirm ? 'All component quantities selected' : ''} placeholder="Select component pieces first" />
              </div>
            ) : !allowMulti ? (
              <div className="mu-fixed">
                <label>Use Qty (Fixed)</label>
                <input
                  readOnly
                  value={Object.keys(selection).length === 1 ? Number(pendingQty || 0).toFixed(2) : ''}
                  placeholder="Select a piece first"
                />
              </div>
            ) : (
              <div className="mu-qty-grid">
                {Object.keys(selection).map((pieceId) => {
                  const piece = displayPieces.find((p) => String(p.pieceId) === String(pieceId));
                  return (
                    <div key={pieceId} className="mu-qty-card">
                      <div><strong>{pieceId}</strong></div>
                      <div className="mu-sub">Current Qty: {Number(piece?.length || 0).toFixed(2)}m</div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={String(piece?.length || 0)}
                        value={selection[pieceId] || ''}
                        onChange={(e) => onQtyChange(pieceId, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mu-chips">
              <span>Selected Qty: {Number(selectedQty || 0).toFixed(2)}m</span>
              <span>Remaining: {Number(remainingQty || 0).toFixed(2)}m</span>
            </div>
            {error && <div className="mu-error">{error}</div>}
          </>
        )}

        <div className="mu-actions">
          <button onClick={onClose} disabled={submitting}>Cancel</button>
          <button onClick={submit} disabled={loading || submitting || !canConfirm}>
            {submitting ? 'Submitting...' : 'Confirm Mark Used'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MarkUsedModal;
